import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { authErrorMessage } from '@/features/auth/turnstile';
import type { Session } from '@supabase/supabase-js';
import { User, UserRole, Phase, MigrationStatus, createDefaultUser, FAST_TRACK_WEEKS } from '@/types/user';
import { setUser as setSentryUser } from '@/lib/observability/sentry';
import posthog from '@/lib/posthog';

interface AuthContextType {
  // Supabase auth state
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  migrationStatusLoading: boolean;
  // captchaToken is optional everywhere: when no Turnstile site key is
  // configured the widget never renders, no token exists, and these calls go
  // out exactly as they did before captcha was added.
  signupWithEmail: (email: string, password: string, firstName?: string, lastName?: string, phone?: string, smsConsent?: boolean, emailConsent?: boolean, captchaToken?: string) => Promise<{ error?: string }>;
  loginWithEmail: (email: string, password: string, captchaToken?: string) => Promise<{ error?: string; needsVerification?: boolean }>;
  resendVerification: (email: string, captchaToken?: string) => Promise<{ error?: string }>;
  logout: () => Promise<void>;

  // Member profile state (in-memory progression for dashboard)
  user: User | null;
  updateUser: (updates: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [migrationStatusLoading, setMigrationStatusLoading] = useState(false);

  // In-memory member profile for dashboard progression state
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    let isMounted = true;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (isMounted) setSession(session);

      // Sync user to GHL on the first sign-in of each browser session.
      // Signup flows are handled by signupWithEmail directly — SIGNED_IN fires
      // synchronously inside supabase.auth.signUp() before signupWithEmail can
      // set the dedup key, so we use a separate flag to suppress this handler
      // during signup and avoid a parallel double-call.
      if ((_event === 'SIGNED_IN' || _event === 'INITIAL_SESSION') && session?.user) {
        const userId = session.user.id;
        const email = session.user.email ?? '';
        const meta = session.user.user_metadata ?? {};
        // Prefer explicit fields stored at signup; fall back to splitting 'name'
        // for users who created accounts before the first/last name change.
        const fullName: string = meta.name ?? '';
        const nameParts = fullName.trim().split(/\s+/).filter(Boolean);
        const first_name: string = meta.first_name ?? nameParts[0] ?? '';
        const last_name: string = meta.last_name ?? nameParts.slice(1).join(' ');

        // Identify on every session (login or page load) so PostHog always
        // knows who the user is and events are attributed correctly.
        posthog.identify(userId, {
          email,
          name: [first_name, last_name].filter(Boolean).join(' ') || email,
        });
        if (_event === 'SIGNED_IN') posthog.capture('user_logged_in');

        const syncKey = `ghl_synced_${userId}`;
        if (!sessionStorage.getItem(syncKey) && !sessionStorage.getItem('ghl_signup_in_progress')) {
          // Set the key now to prevent double-sync if SIGNED_IN fires twice,
          // but clear it on failure so the next login can retry.
          sessionStorage.setItem(syncKey, 'true');

          (async () => {
            // Pass the access token explicitly — functions.invoke may not yet
            // have the session stored internally when this callback fires.
            const { data, error: syncErr } = await supabase.functions.invoke('ghl-sync', {
              body: {
                email,
                first_name,
                last_name,
                phone: meta.phone || '',
                sms_consent: !!meta.sms_consent,
                email_consent: !!meta.email_consent,
              },
              headers: { Authorization: `Bearer ${session.access_token}` },
            });
            if (syncErr) {
              console.error('[GHL] login sync error:', syncErr);
              sessionStorage.removeItem(syncKey); // allow retry on next login
            } else if (data?.contactId) {
              await supabase
                .from('profiles')
                .update({ ghl_contact_id: data.contactId })
                .eq('user_id', userId)
                .is('ghl_contact_id', null);
            }
          })().catch(err => {
            console.error('[GHL] login sync threw:', err);
            posthog.captureException(err);
            sessionStorage.removeItem(syncKey); // allow retry on next login
          });

          supabase.from('member_events').insert({
            user_id: userId,
            event_type: 'member.login',
            properties: { email },
          });
        }
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (isMounted) {
        setSession(session);
        setIsLoading(false);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Tag Sentry events with the current user so error reports are
  // actionable. Cleared on logout. No-op when Sentry isn't configured.
  useEffect(() => {
    if (session?.user) {
      setSentryUser({ id: session.user.id, email: session.user.email });
    } else {
      setSentryUser(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id, session?.user?.email]);

  // Sync in-memory user profile with session
  useEffect(() => {
    if (session?.user) {
      const uid = session.user.id;
      setUser(prev => {
        // If we already have a user for this session, keep their progression state
        if (prev && prev.id === uid) return prev;
        // Create new in-memory profile from session data
        const name = session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'User';
        const email = session.user.email || '';
        return {
          ...createDefaultUser(email, name, 'member'),
          id: uid,
        };
      });

      // Load display name from profiles
      supabase
        .from('profiles')
        .select('name')
        .eq('user_id', uid)
        .single()
        .then(({ data }) => {
          if (data?.name) {
            setUser(prev => prev ? { ...prev, name: data.name } : prev);
          }
        });

      // Load one-time migration status from member_migration.
      // Null means no row exists (not a migrated member).
      setMigrationStatusLoading(true);
      supabase
        .from('member_migration')
        .select('migration_status')
        .eq('user_id', uid)
        .maybeSingle()
        .then(({ data }) => {
          const status = (data?.migration_status as MigrationStatus) ?? null;
          setUser(prev => prev ? { ...prev, migrationStatus: status } : prev);
          setMigrationStatusLoading(false);
        });
    } else {
      setUser(null);
      setMigrationStatusLoading(false);
    }
  }, [session]);

  const signupWithEmail = useCallback(async (email: string, password: string, firstName?: string, lastName?: string, phone?: string, smsConsent?: boolean, emailConsent?: boolean, captchaToken?: string) => {
    const first_name = firstName?.trim() ?? '';
    const last_name = lastName?.trim() ?? '';
    const displayName = [first_name, last_name].filter(Boolean).join(' ') || email.split('@')[0];

    // Block the SIGNED_IN handler from firing GHL sync during signup.
    // SIGNED_IN fires synchronously inside signUp() before we get the
    // session back here, so without this guard both handlers race each other.
    sessionStorage.setItem('ghl_signup_in_progress', 'true');

    const { data, error } = await supabase.auth.signUp({
      email, password,
      options: {
        data: { name: displayName, first_name, last_name, phone: phone || '', sms_consent: !!smsConsent, email_consent: !!emailConsent },
        emailRedirectTo: `${window.location.origin}/login?mode=signin`,
        ...(captchaToken ? { captchaToken } : {}),
      },
    });
    // Always clear the signup guard — signUp() has returned so the SIGNED_IN
    // event will not fire again inside it.
    sessionStorage.removeItem('ghl_signup_in_progress');
    if (error) return { error: authErrorMessage(error.message) };

    // With email verification disabled, signUp returns a session immediately.
    // Sync to GHL here using that token so there are no timing or auth issues.
    // Also set the sessionStorage flag so onAuthStateChange doesn't double-sync.
    if (data.session?.access_token && data.user?.id) {
      const syncKey = `ghl_synced_${data.user.id}`;
      sessionStorage.setItem(syncKey, 'true');

      posthog.identify(data.user.id, {
        email,
        name: displayName,
      });
      posthog.capture('user_signed_up');

      supabase.functions.invoke('ghl-sync', {
        body: { email, first_name, last_name, phone: phone || '', sms_consent: !!smsConsent, email_consent: !!emailConsent },
        headers: { Authorization: `Bearer ${data.session.access_token}` },
      }).then(({ data: syncData, error: syncErr }) => {
        if (syncErr) {
          console.error('[GHL] signup sync error:', syncErr);
          sessionStorage.removeItem(syncKey); // allow retry on next login
          return;
        }
        if (syncData?.contactId && data.user?.id) {
          supabase.from('profiles')
            .update({ ghl_contact_id: syncData.contactId })
            .eq('user_id', data.user.id)
            .is('ghl_contact_id', null)
            .then();
        }
      }).catch(err => {
        console.error('[GHL] signup sync threw:', err);
        posthog.captureException(err, data.user?.id);
        sessionStorage.removeItem(syncKey); // allow retry on next login
      });
    }

    return {};
  }, []);

  const loginWithEmail = useCallback(async (email: string, password: string, captchaToken?: string) => {
    const { error } = await supabase.auth.signInWithPassword(
      captchaToken ? { email, password, options: { captchaToken } } : { email, password }
    );
    if (error) {
      if (error.message.toLowerCase().includes("email not confirmed")) {
        return { needsVerification: true };
      }
      return { error: authErrorMessage(error.message) };
    }
    return {};
  }, []);

  const resendVerification = useCallback(async (email: string, captchaToken?: string) => {
    const { error } = await supabase.auth.resend(
      captchaToken
        ? { type: "signup", email, options: { captchaToken } }
        : { type: "signup", email }
    );
    if (error) return { error: authErrorMessage(error.message) };
    return {};
  }, []);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
  }, []);

  const updateUser = useCallback((updates: Partial<User>) => {
    setUser(prev => prev ? { ...prev, ...updates } : prev);
  }, []);

  const isAuthenticated = !!session;

  return (
    <AuthContext.Provider value={{
      session, isAuthenticated, isLoading, migrationStatusLoading,
      signupWithEmail, loginWithEmail, resendVerification, logout,
      user, updateUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Standard React Context pattern -- Provider + hook in the same file.
// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be within AuthProvider');
  return ctx;
};
